import { useState } from 'react'
import styled from 'styled-components'

import UserSummary from 'checkout/components/AuthPanel/UserSummary'
import CheckoutPanel from 'checkout/components/CheckoutPanel'
import ExpressCheckout from 'checkout/components/ExpressCheckoutPanel/ExpressCheckout'
import { useCheckoutContext } from 'checkout/containers/CheckoutContextProvider'
import { EXPERIMENT_NAME } from 'experiments/constants/Experiments'
import {
  useExperimentState,
  useLogExperiment,
  useStartExperiment,
} from 'experiments/hooks/useExperiment'
import { useFeatureFlag } from 'featureFlags/hooks/useFeatureFlag'
import { t } from 'localization'
import CreateAccountForm from 'login/components/CreateAccountForm'
import LoginForm from 'login/components/LoginForm'
import Button from 'shared/components/Button'
import Divider from 'shared/components/Divider'
import { CheckoutQA } from 'shared/dataAttributes'
import { FeatureFlag } from 'shared/enums/FeatureFlag'
import { fonts, media } from 'shared/lib'

type LoginView = 'login' | 'createAccount'

const TP = 'checkout.components.AuthPanel'

const GuestCheckoutAuth = () => {
  const { continueAsGuest, createOrder, updatePanel, userType } = useCheckoutContext()
  /** Handles which login view is displayed: Login vs. Create Account */
  const [loginView, setLoginView] = useState<LoginView>('login')
  /** FF to enable the create account option */
  const isCreateAccountEnabled = useFeatureFlag(
    FeatureFlag.TEMP_WEB_FC_ENABLE_CREATE_ACCOUNT_GUEST_CHECKOUT,
  )
  // Experiment setup for create account option
  const createAccountExperiment: EXPERIMENT_NAME = 'web_fc_create_account_guest_checkout'
  const enableExperiments = useFeatureFlag(FeatureFlag.WEB_FC_ENABLE_EXPERIMENTS)
  useStartExperiment(createAccountExperiment, !isCreateAccountEnabled)
  useLogExperiment(createAccountExperiment, !isCreateAccountEnabled)
  const experimentState = useExperimentState()

  /** Indicates experiment flags are enabled and user is bucketed in create account variant */
  const displayCreateAccount =
    enableExperiments &&
    experimentState[createAccountExperiment]?.groupAssigned === 'createAccount' &&
    isCreateAccountEnabled

  const handleError = (errors: string[]) => {
    updatePanel('guestCheckoutAuth', { loading: false, error: errors?.[0] })
  }

  const toggleLoginView = () => {
    setLoginView(loginView === 'login' ? 'createAccount' : 'login')
  }

  return (
    <CheckoutPanel
      editable={false}
      expandedContent={
        <>
          <StyledButton
            data-qa={CheckoutQA.CheckoutAsGuestButton}
            buttonType="secondary"
            $fill
            onClick={continueAsGuest}
          >
            {t(`${TP}.checkoutAsGuest`, 'Check out as guest')}
          </StyledButton>
          <ExpressCheckout noTopMargin />
          <Divider />
          <Subtitle data-qa={CheckoutQA.DesktopCheckoutWithFlightClub}>
            {t(`${TP}.checkoutWithFCAccount`, 'Check out with your Flight Club account')}
          </Subtitle>
          <SubtitleMobile data-qa={CheckoutQA.MobileCheckoutWithFlightClub}>
            {t(`${TP}.checkoutWithFC`, 'Check out with Flight Club')}
          </SubtitleMobile>
          {loginView === 'login' && (
            <LoginForm
              createAccountInsteadStyle="linkAndDescription"
              onCreateAccountClick={toggleLoginView}
              isCreateAccountOptionHidden={!displayCreateAccount}
              onLogin={createOrder}
              onLoginError={handleError}
            />
          )}
          {loginView === 'createAccount' && displayCreateAccount && (
            <CreateAccountForm
              onCreateAccount={createOrder}
              onCreateAccountError={handleError}
              location="during_checkout"
              loginInsteadStyle="linkAndDescription"
              onLoginClick={toggleLoginView}
            />
          )}
        </>
      }
      name="guestCheckoutAuth"
      qaAttr={CheckoutQA.LoginCreateAccount}
      summarizedContent={
        <>
          <UserSummary />
          <ExpressCheckout forceTopMargin />
        </>
      }
      title={
        userType === 'loggedInUser'
          ? t(`${TP}.flightClubAccount`, 'Flight Club account')
          : t(`${TP}.account`, 'account')
      }
    />
  )
}

const Subtitle = styled.h2`
  display: none;
  ${media.medium`
    display: block;
    ${fonts.SUBTITLE_2}
    margin-bottom: 20px;
  `}
`
const SubtitleMobile = styled.h2`
  display: block;
  ${fonts.SUBTITLE_2}
  margin-bottom: 20px;
  ${media.medium`
    display: none;
  `}
`
const StyledButton = styled(Button)`
  margin: 20px 0 15px;
`

export default GuestCheckoutAuth
